/* eslint-disable react/no-multi-comp */
import React from 'react';
import PropTypes from 'prop-types';
import _pick from 'lodash/pick';
import className from 'classnames';
import styles from './css/EnhancedTextInput.styl';
import Clear from './graphics/Clear';

// https://developer.mozilla.org/en-US/docs/Web/HTML/Element/Input#Attributes_common_to_all_input_types
const validInputAttributes = [
  'autoComplete',
  'autoFocus',
  'disabled',
  'form',
  'list',
  'name',
  'readOnly',
  'tabIndex',
  'type',
  'value',
  'maxLength',
  'minLength',
];

/**
 * Gets display name of component
 *
 * @param {Component} WrappedComponent Component to get name from
 * @return {*|string} display name of component.
 */
function getDisplayName(WrappedComponent) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

export const controlled = function(WrappedComponent) {
  class WrappedInput extends React.Component {
    static propTypes = {
      validate: PropTypes.func,
      onChange: PropTypes.func,
      defaultValue: PropTypes.string,
    };

    static defaultProps = {
      defaultValue: '',
    };

    constructor(props) {
      super(props);

      this.state = {
        focus: false,
        touched: false,
        dirty: false,
        error: undefined, // eslint-disable-line
        value: '',
      };

      this.handleFocus = this.handleFocus.bind(this);
      this.handleBlur = this.handleBlur.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleClear = this.handleClear.bind(this);
      this.validateField = this.validateField.bind(this);
    }

    handleFocus() {
      this.setState({
        focus: true,
      });
    }

    handleBlur(evt) {
      const {
        currentTarget: {value},
      } = evt;

      this.setState({
        hasFocus: false,
        error: this.validateField(value),
        touched: true,
      });
    }

    handleClear() {
      this.handleChange({
        currentTarget: {value: ''},
        target: {value: ''},
      });
    }

    validateField(value) {
      let error; // eslint-disable-line
      const {validate, required} = this.props;

      if (required && !value) {
        error = 'This field is required.';
      }

      if (validate) {
        error = validate(value);
      }

      return error;
    }

    handleChange(evt) {
      const {onChange, defaultValue} = this.props;
      const {
        currentTarget: {value},
      } = evt;

      if (onChange) {
        onChange(evt);
      }

      const dirty = value !== defaultValue;

      this.setState({
        error: null,
        dirty,
        value,
      });
    }

    render() {
      return (
        <WrappedComponent
          {...this.props}
          {...this.state}
          onChange={this.handleChange}
          onBlur={this.handleBlur}
          onFocus={this.handleFocus}
          onClear={this.handleClear}
        />
      );
    }
  }

  WrappedInput.displayName = `Controlled(${getDisplayName(WrappedComponent)}`;

  return WrappedInput;
};

/** EnhancedTextInput component
 * Has a placeholder and label that shifts upon receiving input
 */
export class EnhancedTextInput extends React.Component {
  static propTypes = {
    name: PropTypes.string.isRequired,
    title: PropTypes.string,
    required: PropTypes.bool,
    touched: PropTypes.bool,
    error: PropTypes.string,
    focus: PropTypes.bool,
    value: PropTypes.string,
    onClear: PropTypes.func,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    defaultValue: PropTypes.string,
    placeholder: PropTypes.string,
  };

  static defaultProps = {
    type: 'text',
    tabIndex: '0',
    title: '',
    placeholder: '',
    disabled: false,
  };

  render() {
    const {
      name,
      title,
      required,
      touched,
      error,
      focus,
      value,
      onClear,
      onChange,
      onBlur,
      onFocus,
      defaultValue,
      placeholder,
    } = this.props;

    const hasValue = Boolean(value);
    const showError = touched && error;
    const dirty = hasValue && defaultValue !== value;
    const blockClassNames = className({
      Field: true,
      'has-error': showError,
      'has-focus': focus,
      'is-dirty': dirty,
    });

    const fieldTitle = required ? `${title}*` : title;

    return (
      <div className={styles.FieldWrap}>
        <div className={blockClassNames}>
          <label className="FieldLabel" htmlFor={name}>
            {fieldTitle}
          </label>

          <input
            {..._pick(this.props, validInputAttributes)}
            id={`field-${name}`}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            name={name}
            placeholder={placeholder || fieldTitle}
            className={styles.FieldControl}
          />

          {dirty && (
            <button
              type="button"
              data-id="clear-button"
              className={styles.FieldIcon}
              onClick={onClear}
              tabIndex={-1}
            >
              <Clear size="15" fillColor="#999" />
            </button>
          )}
        </div>

        {showError && <div className={styles.FieldError}>{error}</div>}
      </div>
    );
  }
}

export default controlled(EnhancedTextInput);



// WEBPACK FOOTER //
// ./src/reactModules/shared/library/FormControls/EnhancedTextInput.js